import {
  FiatPrice,
  priceFreezeApi,
  PriceFreezeRefundFulfillResponseEnum,
  PriceFreezeRefundPollFulfillResponse,
  PriceFreezeRefundPollFulfillResponseEnum,
  PriceFreezeRefundQuoteResponseEnum,
  RefundPriceFreezeResponseEnum,
} from "@b2bportal/price-freeze-api";
import { useI18nContext } from "@hopper-b2b/i18n";
import { CallState } from "@hopper-b2b/types";
import { B2BLoadingPopup, ErrorModal } from "@hopper-b2b/ui";
import axios from "axios";
import { useCallback, useMemo, useState } from "react";

export interface IPriceFreezeExerciseErrorModal {
  priceFreezeId: string;
  handleTryAgainCheckout: () => void;
  handleContinue: () => void;
  handleContactCustomerService: () => void;
  openModal: boolean;
  priceFreezeTravelerQuantity: number;
  priceFreezeTotalAmount?: FiatPrice;
  className?: string;
  loadingClassName?: string;
  secondaryMessage?: string;
  apiVersion?: "V0" | "V3";
}

export const PriceFreezeExerciseErrorModal = (
  props: IPriceFreezeExerciseErrorModal
) => {
  const { t, formatFiatCurrency } = useI18nContext();

  const {
    priceFreezeId,
    handleTryAgainCheckout,
    handleContinue,
    handleContactCustomerService,
    openModal,
    priceFreezeTravelerQuantity,
    priceFreezeTotalAmount,
    className,
    loadingClassName,
    secondaryMessage,
    apiVersion = "V0",
  } = props;

  const amountText = priceFreezeTotalAmount
    ? formatFiatCurrency(priceFreezeTotalAmount)
    : "";
  const [callState, setCallState] = useState(CallState.NotCalled);

  const callRefundPriceFreeze = useCallback(async () => {
    setCallState(CallState.InProcess);
    try {
      if (apiVersion === "V3") {
        const res = await priceFreezeApi(
          axios
        ).apiV3PricefreezeVoucherRefundQuotePost({
          priceFreezeId,
        });
        if (
          res.data.PriceFreezeRefundQuoteResponse ===
          PriceFreezeRefundQuoteResponseEnum.Success
        ) {
          const token = res.data.token;
          const { data: quoteResponse } = await priceFreezeApi(
            axios
          ).apiV3PricefreezeVoucherRefundFulfillPost({
            token,
          });
          if (
            quoteResponse.PriceFreezeRefundFulfillResponse ===
            PriceFreezeRefundFulfillResponseEnum.Success
          ) {
            let pollResponse: PriceFreezeRefundPollFulfillResponse = {
              PriceFreezeRefundPollFulfillResponse:
                PriceFreezeRefundPollFulfillResponseEnum.Pending,
            };

            while (
              pollResponse.PriceFreezeRefundPollFulfillResponse ===
              PriceFreezeRefundPollFulfillResponseEnum.Pending
            ) {
              await new Promise((resolve) => setTimeout(resolve, 2000));
              const { data } = await priceFreezeApi(
                axios
              ).apiV3PricefreezeVoucherRefundFulfillPollPost({
                token,
              });
              pollResponse = data;
            }
            if (
              pollResponse.PriceFreezeRefundPollFulfillResponse ===
              PriceFreezeRefundPollFulfillResponseEnum.Success
            ) {
              setCallState(CallState.Success);
            } else {
              setCallState(CallState.Failed);
            }
          } else {
            setCallState(CallState.Failed);
          }
        } else {
          setCallState(CallState.Failed);
        }
      } else {
        const res = await priceFreezeApi(axios).apiV0PricefreezeRefundPost({
          id: priceFreezeId,
        });
        if (
          res.data.RefundPriceFreezeResponse ===
          RefundPriceFreezeResponseEnum.Success
        ) {
          setCallState(CallState.Success);
        } else {
          setCallState(CallState.Failed);
        }
      }
    } catch (e) {
      setCallState(CallState.Failed);
    }
  }, [apiVersion, priceFreezeId]);

  const priceFreezeRefundLoading = useMemo(() => {
    return callState === CallState.InProcess;
  }, [callState]);

  const errorModalProps = useMemo(() => {
    switch (callState) {
      case CallState.Success:
        return {
          title: t("priceFreezeExercise.priceFreezeRefundSuccess.title") ?? "",
          subtitle: t("priceFreezeExercise.priceFreezeRefundSuccess.subtitle"),
          primaryButtonText: t(
            "priceFreezeExercise.priceFreezeRefundSuccess.primaryButton"
          ),
          primaryOnClick: handleContinue,
        };
      case CallState.Failed:
        return {
          title: t("priceFreezeExercise.priceFreezeRefundFail.title") ?? "",
          subtitle: t("priceFreezeExercise.priceFreezeRefundFail.subtitle"),
          primaryButtonText: t(
            "priceFreezeExercise.priceFreezeRefundFail.primaryButton"
          ),
          primaryOnClick: callRefundPriceFreeze,
          secondaryButtonText: t(
            "priceFreezeExercise.priceFreezeRefundFail.secondaryButton"
          ),
          secondaryOnClick: handleContactCustomerService,
        };
      default:
        return {
          title: t("priceFreezeExercise.priceFreezeExerciseError.title", {
            numberOfTravelers: priceFreezeTravelerQuantity,
          }),
          subtitle: t("priceFreezeExercise.priceFreezeExerciseError.subtitle", {
            currencyAmount: amountText,
            numberOfTravelers: priceFreezeTravelerQuantity,
          }),
          primaryButtonText: t(
            "priceFreezeExercise.priceFreezeExerciseError.primaryButton"
          ),
          primaryOnClick: callRefundPriceFreeze,
          secondaryButtonText: t(
            "priceFreezeExercise.priceFreezeExerciseError.secondaryButton"
          ),
          secondaryOnClick: handleTryAgainCheckout,
        };
    }
  }, [
    callState,
    t,
    handleContinue,
    callRefundPriceFreeze,
    handleContactCustomerService,
    priceFreezeTravelerQuantity,
    amountText,
    handleTryAgainCheckout,
  ]);

  return priceFreezeRefundLoading ? (
    <B2BLoadingPopup
      open={priceFreezeRefundLoading}
      fullScreen={true}
      message={t("priceFreezeExercise.loading")}
      secondaryMessage={secondaryMessage}
      popupSize={"mobile"}
      className={loadingClassName}
    />
  ) : (
    <ErrorModal open={openModal} {...errorModalProps} className={className} />
  );
};
